/**
 * 管理端会员页共享类型。
 * 字段命名对齐后端响应（经 ResponseInterceptor + admin client 双重 camelCase 转换）。
 */

/** 会员等级行（GET /admin/membership/levels） */
export interface LevelRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/** 功能权限行（GET /admin/membership/features） */
export interface FeatureRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  groupName: string | null;
  isSystem: boolean;
  sortOrder: number;
}

/** 对比矩阵中的等级摘要 */
export interface CompareLevel {
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  sortOrder: number;
}

/** 对比矩阵中的功能行（access 为各等级开关） */
export interface CompareFeature {
  key: string;
  name: string;
  groupName: string | null;
  description: string | null;
  access: Array<{ levelCode: string; allowed: boolean }>;
}

/** 对比矩阵响应（GET /admin/membership/level-features/compare） */
export interface CompareResponse {
  levels: CompareLevel[];
  features: CompareFeature[];
}

/**
 * 会员套餐行（GET /admin/membership/plans），与后端 AdminPlanRow 逐字对应：
 * 用户端套餐形状（GET /payment/plans）加上管理端独有的 id / isActive / sortOrder。
 * 金额已由后端 Number() 归一，与用户端同为 number，不再是 Decimal 字符串。
 */
export interface PlanRow {
  id: string;
  planCode: string;
  levelCode: string;
  name: string;
  /** 值域与后端 CreatePlanDto 的 @IsIn 一致（多加 `| string` 会让联合塌缩成 string） */
  billingCycle: 'month' | 'quarter' | 'year';
  durationMonths: number;
  price: number;
  /** 续费价，null 表示与首购价一致 */
  renewPrice: number | null;
  autoRenewSupported: boolean;
  isActive: boolean;
  sortOrder: number;
}

/** 订阅用户行（GET /admin/membership/subscriptions 的 items） */
export interface SubscriptionRow {
  id: string;
  username: string | null;
  fullName: string;
  levelCode: string;
  levelName: string;
  expireAt: string | null;
  autoRenew: boolean;
  status: string | null;
}

/** 订阅列表响应 */
export interface SubscriptionListResponse {
  items: SubscriptionRow[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 活动行（GET /admin/campaigns/:id 与列表 items），与后端 CampaignRow 逐字对应。
 * benefitText 与 pageUrl 都是服务端拼好的：链接基址取自 FRONTEND_URL，
 * 后台在 admin 子域，绝不能用 window.location.origin 现拼（复制出来会指向后台自己）。
 */
export interface CampaignRow {
  id: string;
  key: string;
  title: string;
  subtitle: string | null;
  heroImage: string | null;
  bodyMd: string | null;
  benefitUnit: 'day' | 'month' | null;
  benefitValue: number | null;
  benefitText: string | null;
  startAt: string | null;
  endAt: string | null;
  maxGrants: number;
  grantedCount: number;
  isActive: boolean;
  noindex: boolean;
  pageUrl: string;
}

/** 活动列表响应 */
export interface CampaignListResponse {
  items: CampaignRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** 发放记录一行（GET /admin/membership/grants）：sourceLabel / valueText 由服务端拼好下发 */
export interface GrantRow {
  id: string;
  partyId: string;
  partyName: string | null;
  source: string;
  sourceLabel: string;
  campaignKey: string | null;
  levelCode: string;
  unit: string;
  value: number;
  valueText: string;
  expireBefore: string | null;
  expireAfter: string;
  reason: string | null;
  operatorName: string | null;
  createdAt: string;
}

/** 发放记录列表响应 */
export interface GrantListResponse {
  items: GrantRow[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 计酬状态。值域与后端 ReferralRecord.status 的实际写入值一致：
 * 「达标但不发放」（未达最低实付额 / 超月度计酬上限）仍是 qualified，靠 reason 区分。
 */
export type ReferralStatus = 'pending' | 'qualified' | 'rewarded';

/** 邀请记录一行（GET /admin/referral/records） */
export interface AdminReferralRecord {
  id: string;
  referrerId: string;
  referrerName: string;
  inviteeId: string;
  inviteeName: string;
  referralCode: string;
  orderId: string | null;
  status: ReferralStatus;
  /** 不计酬原因（below_min_amount / monthly_cap），无则为 null */
  reason: string | null;
  rewardMonths: number;
  createdAt: string;
  updatedAt: string;
}

/** 邀请记录列表响应，形状与其余列表端点统一为 { items, total, page, pageSize } */
export interface AdminReferralListResponse {
  items: AdminReferralRecord[];
  total: number;
  page: number;
  pageSize: number;
}

/** 计费周期展示映射 */
export const BILLING_CYCLE_LABEL: Record<string, string> = {
  month: '按月',
  quarter: '按季度',
  year: '按年',
};

/**
 * 会员等级 code → AntD Tag 配色。
 * 等级由后台增删（GET /admin/membership/levels），未收录的 code 一律回落 'blue'，
 * 因此这里只登记种子等级的既定配色，不要为「将来可能新增的等级」预留条目。
 */
export const LEVEL_COLOR: Record<string, string> = {
  free: 'default',
  premium: 'gold',
};
